import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reducedMotion } from './effects';
import { bootScript, matrix, donut, top as topProgram, snake } from './programs';

// The hero terminal, but real: it takes commands.
//
// A screenshot of a terminal is a picture of a thing. This is the thing. Every
// figure it prints comes from the same generated stats and history files the
// rest of the site draws from, so `crates`, `syscalls` and `ls` are readings of
// the kernel repository rather than a script of pretend output.

const n = (value) => value.toLocaleString('en-US');
const k = (value) => `${Math.round(value / 1000)}K`;

// Output is a list of tagged lines. The tag picks the colour; the text is
// plain, so nothing the reader types can inject markup into the transcript.
const L = (text = '', tone = 'out') => ({ text, tone });

const FERRIS = [
  '     _~^~^~_',
  ' \\) /  o o  \\ (/',
  "   '_   ¬   _'",
  "   / '-----' \\",
];

const FORTUNES = [
  'Every unsafe block is a promise you made to yourself at 2am.',
  'The compiler is not arguing with you. It is the only one reading carefully.',
  'A kernel is just a very opinionated library with a bad temper.',
  'Rewrite it in Rust, they said. So we did. All of it.',
  'Iteration four exists because iterations one through three taught us things.',
  'There is no such thing as a small change to the scheduler.',
  'The fastest syscall is the one that returns before you finish reading it.',
  'Vibe-coded, human-directed, and still it panics like anything else.',
];

// Fetches are memoised across the life of the page: run `crates` twice and the
// second one answers instantly.
const cache = new Map();
function load(file) {
  if (!cache.has(file)) {
    cache.set(file, fetch(`/data/${file}`).then((response) => {
      if (!response.ok) throw new Error(`${response.status} ${file}`);
      return response.json();
    }));
  }
  return cache.get(file);
}

// ---------------------------------------------------------------- virtual fs
// The workspace really is a tree, so `cd` and `ls` walk the real crate paths
// rather than a made-up filesystem. A directory with a Cargo.toml behind it is
// a crate, and `cat` on one prints that crate's own numbers.
function buildTree(crates) {
  const root = { name: '', path: '', dirs: new Map(), crate: null };
  for (const crate of crates) {
    let node = root;
    for (const part of crate.path.split('/')) {
      if (!node.dirs.has(part)) {
        node.dirs.set(part, { name: part, path: node.path ? `${node.path}/${part}` : part, dirs: new Map(), crate: null });
      }
      node = node.dirs.get(part);
    }
    node.crate = crate;
  }
  return root;
}

function resolve(root, cwd, target) {
  const parts = (target ?? '').startsWith('/') ? [] : cwd.split('/').filter(Boolean);
  for (const part of (target ?? '').split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') { parts.pop(); continue; }
    parts.push(part);
  }
  let node = root;
  for (const part of parts) {
    node = node.dirs.get(part);
    if (!node) return null;
  }
  return node;
}

// ------------------------------------------------------------------ commands
// Each handler gets the parsed arguments and a context of everything it is
// allowed to touch. Returning lines prints them; anything that takes time uses
// ctx.print so the reader watches it happen instead of waiting for a block.
function makeCommands(stats) {
  const months = Math.max(1, Math.round(stats.days / 30.44));

  const commands = {
    help: {
      brief: 'everything this shell answers to',
      run: () => {
        const listed = Object.entries(commands).filter(([, spec]) => !spec.hidden);
        const line = ([name, spec]) => L(`  ${name.padEnd(12)} ${spec.brief}`);
        return [
          L('programs', 'hot'),
          ...listed.filter(([, spec]) => spec.kind === 'program').map(line),
          L(),
          L('commands', 'hot'),
          ...listed.filter(([, spec]) => spec.kind !== 'program').map(line),
          L(),
          L('  tab completes · up and down walk your history · ctrl-l clears', 'dim'),
          L('  a few things are not on this list. poke around.', 'dim'),
        ];
      },
    },

    info: {
      brief: 'what this project is',
      run: () => [
        L('$ oxide info', 'cmd'),
        L('  kernel      oxide · rust · from-scratch linux-abi'),
        L('  iteration   4'),
        L('  arch        arm64, x86_64'),
        L(`  commits     ${n(stats.commits)} over ${stats.days} days`),
        L(`  crates      ${n(stats.crates)} · ${n(stats.rustFiles)} modules · ${k(stats.rustLoc)} lines`),
        L(`  syscalls    ${n(stats.syscalls)} implemented`),
        L('  drivers     virtualized (hardware: in progress)'),
        L('  status      experimental', 'hot'),
      ],
    },

    stats: {
      brief: 'the numbers, all of them',
      run: () => [
        L(`  ${n(stats.commits)} commits`.padEnd(28) + `${n(stats.merges)} merges`),
        L(`  ${n(stats.rustLoc)} lines of rust`.padEnd(28) + `${n(stats.rustFiles)} rust files`),
        L(`  ${n(stats.functions)} functions`.padEnd(28) + `${n(stats.types)} types`),
        L(`  ${n(stats.tests)} tests`.padEnd(28) + `${n(stats.unsafeBlocks)} unsafe blocks`),
        L(`  ${n(stats.syscalls)} syscalls`.padEnd(28) + `${n(stats.specs)} specs (${stats.specsFrozen} frozen)`),
        L(`  +${n(stats.linesAdded)} / -${n(stats.linesRemoved)} lines moved`, 'dim'),
        L(),
        L(`  ${months} months. one head. a lot of machines.`, 'dim'),
      ],
    },

    uname: {
      brief: 'who is running',
      run: (args) => (args.includes('-a')
        ? [L(`Oxide 4.0.0-oxide #${stats.commits} SMP ${stats.lastCommit} aarch64 x86_64 GNU/Linux-ABI`)]
        : [L('Oxide')]),
    },

    neofetch: {
      brief: 'the obligatory one',
      run: () => {
        const facts = [
          'guest@oxide',
          '─────────────────────────',
          `OS       Oxide 4.0.0 (iteration 4)`,
          `Kernel   rust, from scratch`,
          `Uptime   ${stats.days} days of commits`,
          `Packages ${n(stats.crates)} crates`,
          `Shell    ox-sh 1.0`,
          `Arch     arm64 · x86_64`,
          `Memory   safe by construction`,
        ];
        const rows = Math.max(FERRIS.length, facts.length);
        const out = [];
        for (let row = 0; row < rows; row += 1) {
          out.push(L(`  ${(FERRIS[row] ?? '').padEnd(20)}${facts[row] ?? ''}`, row < FERRIS.length ? 'hot' : 'out'));
        }
        return out;
      },
    },

    syscalls: {
      brief: 'list the linux syscalls implemented [filter]',
      run: async (args, ctx) => {
        ctx.print([L('reading the syscall table…', 'dim')]);
        const data = await load('history-syscalls.json');
        const filter = args[0]?.toLowerCase();
        const live = data.slots.filter((slot) => slot.gone == null && (!filter || slot.name.includes(filter)));
        if (!live.length) return [L(`no syscall matches "${filter}"`, 'warn')];
        const out = [L(`${live.length} syscall${live.length === 1 ? '' : 's'}${filter ? ` matching "${filter}"` : ' implemented'}`, 'hot'), L()];
        for (let at = 0; at < live.length; at += 4) {
          out.push(L('  ' + live.slice(at, at + 4).map((slot) => `${String(slot.nr).padStart(3)} ${slot.name}`.padEnd(24)).join('').trimEnd()));
        }
        return out;
      },
    },

    crates: {
      brief: 'the biggest crates in the workspace [filter]',
      run: async (args, ctx) => {
        ctx.print([L('reading the workspace…', 'dim')]);
        const tree = await ctx.workspace();
        const filter = args[0]?.toLowerCase();
        const rows = tree.crates
          .filter((crate) => !filter || crate.path.toLowerCase().includes(filter))
          .sort((a, b) => b.loc - a.loc)
          .slice(0, 20);
        if (!rows.length) return [L(`nothing in the workspace matches "${filter}"`, 'warn')];
        return [
          L(`  ${'crate'.padEnd(34)}${'lines'.padStart(9)}${'commits'.padStart(10)}`, 'dim'),
          ...rows.map((crate) => L(`  ${crate.path.padEnd(34)}${n(crate.loc).padStart(9)}${n(crate.commits).padStart(10)}`)),
          L(),
          L(`  ${n(tree.crates.length)} crates have existed here. cd into one and cat it.`, 'dim'),
        ];
      },
    },

    ls: {
      brief: 'list the workspace here',
      run: async (args, ctx) => {
        const tree = await ctx.workspace();
        const node = resolve(tree.root, ctx.cwd, args[0] ?? '.');
        if (!node) return [L(`ls: ${args[0]}: no such directory`, 'warn')];
        const dirs = [...node.dirs.values()];
        if (!dirs.length) return [L(`${node.path || '/'} is a leaf crate. try \`cat\`.`, 'dim')];
        const out = [];
        for (let at = 0; at < dirs.length; at += 3) {
          out.push(L('  ' + dirs.slice(at, at + 3)
            .map((dir) => `${dir.name}${dir.crate ? '' : '/'}`.padEnd(28)).join('').trimEnd(),
          ));
        }
        return out;
      },
    },

    cd: {
      brief: 'walk into a crate',
      run: async (args, ctx) => {
        const tree = await ctx.workspace();
        const node = resolve(tree.root, ctx.cwd, args[0] ?? '/');
        if (!node) return [L(`cd: ${args[0]}: no such directory`, 'warn')];
        ctx.setCwd(node.path);
        return node.crate ? [L(`${node.path}, ${n(node.crate.loc)} lines. \`cat\` it.`, 'dim')] : [];
      },
    },

    cat: {
      brief: 'read a crate\'s own numbers',
      run: async (args, ctx) => {
        const tree = await ctx.workspace();
        const node = resolve(tree.root, ctx.cwd, args[0] ?? '.');
        if (!node) return [L(`cat: ${args[0]}: not found`, 'warn')];
        if (!node.crate) return [L(`cat: ${node.path || '/'} is a directory`, 'warn')];
        const c = node.crate;
        return [
          L(`  ${c.path}`, 'hot'),
          L(`  system      ${c.sys}`),
          L(`  lines       ${n(c.loc)}`),
          L(`  commits     ${n(c.commits)}`),
          L(`  churn       +${n(c.added)} / -${n(c.removed)}`),
          L(`  born        commit ${n(c.born)} · last touched ${n(c.last)}`),
        ];
      },
    },

    log: {
      brief: 'the milestones, in order',
      run: async (args, ctx) => {
        ctx.print([L('reading the history…', 'dim')]);
        const data = await load('history-milestones.json');
        const rows = data.milestones.slice(-14);
        return rows.map((row) => L(`  ${String(row.index).padStart(6)}  ${row.label}`));
      },
    },

    valley: {
      brief: 'walk the kernel as a place',
      run: (args, ctx) => {
        ctx.print([L('mounting 34,000 trees…', 'dim')]);
        setTimeout(() => ctx.go('/valley'), 700);
        return [];
      },
    },

    growth: {
      brief: 'every commit, charted',
      run: (args, ctx) => {
        ctx.print([L('replaying the history…', 'dim')]);
        setTimeout(() => ctx.go('/growth'), 700);
        return [];
      },
    },

    neon: {
      brief: 'flip the whole page to the neon hour',
      run: (args, ctx) => {
        const on = ctx.toggleNeon();
        return [L(on ? 'neon hour. the valley does this too, properly.' : 'back to daylight.', 'hot')];
      },
    },

    build: {
      brief: 'compile it (not really)',
      run: async (args, ctx) => {
        const tree = await ctx.workspace().catch(() => null);
        const picks = tree
          ? [...tree.crates].sort(() => Math.random() - 0.5).slice(0, 6)
          : ['kernel', 'drivers', 'arch', 'shared', 'kpi', 'userspace'].map((name) => ({ name }));
        for (const crate of picks) {
          ctx.print([L(`   Compiling ${crate.name ?? crate} v4.0.0`, 'dim')]);
          await ctx.wait(reducedMotion() ? 0 : 220);
        }
        return [
          L('    Finished `release` profile [optimized] in 41.7s', 'ok'),
          L(`    ${n(stats.tests)} tests are waiting for you upstairs.`, 'dim'),
        ];
      },
    },

    // The things that take the screen. `kind` is what sorts them into their
    // own block in `help`, because a list where `snake` sits between `pwd` and
    // `stats` buries the only line worth reading.
    snake:  { kind: 'program', brief: 'a game. arrows to steer', run: (args, ctx) => { ctx.launch('snake'); return []; } },
    donut:  { kind: 'program', brief: 'the spinning torus, in ascii', run: (args, ctx) => { ctx.launch('donut'); return []; } },
    matrix: { kind: 'program', brief: 'rain', run: (args, ctx) => { ctx.launch('matrix'); return []; } },
    top:    { kind: 'program', brief: 'live load across the subsystems', run: (args, ctx) => { ctx.launch('top'); return []; } },

    boot: {
      kind: 'program',
      brief: 'watch the kernel come up',
      run: async (args, ctx) => {
        for (const line of bootScript(stats)) {
          ctx.print([line]);
          await ctx.wait(reducedMotion() ? 0 : 130);
        }
        return [L('oxide login: guest', 'hot'), L('welcome back.', 'dim')];
      },
    },

    fortune: { brief: 'kernel wisdom, questionable', run: () => [L(`  ${FORTUNES[Math.floor(Math.random() * FORTUNES.length)]}`, 'hot')] },
    whoami: { brief: 'you', run: () => [L('guest. unprivileged, and welcome.')] },
    date: { brief: 'now', run: () => [L(new Date().toString())] },
    echo: { brief: 'say it back', run: (args) => [L(args.join(' '))] },
    clear: { brief: 'wipe the screen', run: (args, ctx) => { ctx.clear(); return []; } },

    source: { brief: 'open the source', run: (args, ctx) => { ctx.print([L('opening watkins-labs/oxide on GitFoundry…', 'dim')]); open('https://gitfoundry.watkinsx.com/watkins-labs/oxide', '_blank', 'noopener'); return []; } },
    github: { hidden: true, brief: 'moved', run: (args, ctx) => { ctx.print([L('the source moved to GitFoundry; opening it…', 'dim')]); open('https://gitfoundry.watkinsx.com/watkins-labs/oxide', '_blank', 'noopener'); return []; } },

    // Not in `help`. Finding these is the point.
    sudo: {
      hidden: true,
      brief: 'no',
      run: (args, ctx) => {
        if (args.join(' ').replace(/\s+/g, ' ').startsWith('rm -rf /')) {
          ctx.panic('sudo rm -rf /');
          return [];
        }
        return [L('guest is not in the sudoers file. This incident has been reported.', 'warn')];
      },
    },
    panic: { hidden: true, brief: 'break it', run: (args, ctx) => { ctx.panic('deliberate panic()'); return []; } },
    ferris: { hidden: true, brief: 'hello', run: () => FERRIS.map((row) => L(`  ${row}`, 'hot')) },
    exit: { hidden: true, brief: 'leave', run: () => [L('there is no exit. there is only the kernel.', 'dim')] },
    rm: { hidden: true, brief: 'no', run: () => [L('rm: permission denied. honestly, good.', 'warn')] },
    vim: { hidden: true, brief: 'no', run: () => [L('you are already trapped in something. one at a time.', 'dim')] },
    pwd: { hidden: true, brief: 'where', run: (args, ctx) => [L(`/${ctx.cwd}`)] },
  };
  return commands;
}

// ------------------------------------------------------------------ the shell
export default function Shell({ stats, onNavigate, onNeon, onPanic }) {
  const commands = useMemo(() => makeCommands(stats), [stats]);
  const [lines, setLines] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [cwd, setCwd] = useState('');
  const [booted, setBooted] = useState(false);
  const [frame, setFrame] = useState(null);
  const running = useRef(null);
  const history = useRef([]);
  const pending = useRef([]);
  const historyAt = useRef(-1);
  const body = useRef(null);
  const field = useRef(null);
  const shell = useRef(null);

  const print = useCallback((next) => setLines((prior) => [...prior, ...next]), []);

  // The workspace tree is worth 330 KB, so it is only fetched the first time a
  // command actually needs to walk it.
  const workspace = useRef(null);
  const tasks = useRef(null);
  const getWorkspace = useCallback(async () => {
    if (!workspace.current) {
      // Only the entries that really carry a Cargo.toml. The history file also
      // tracks loose directories like `docs` and `scratch`, and `ls` listing
      // those as crates would be a lie the rest of the page does not tell.
      workspace.current = load('history-crates.json').then((data) => {
        const crates = data.crates.filter((crate) => crate.cargo);
        // Once the workspace is here, `top` can run on the crates that really
        // carry the project rather than on a fixed list.
        tasks.current = [...crates].sort((one, two) => two.commits - one.commits).slice(0, 8)
          .map((crate) => ({ name: crate.path.replace(/^crates\//, ''), weight: 12 + (crate.commits / crates[0].commits) * 70 }));
        return { crates, root: buildTree(crates) };
      });
    }
    return workspace.current;
  }, []);

  // How many characters actually fit. A program handed a guessed 80x24 draws
  // half of itself past the edge of the panel, so the grid is measured from
  // the rendered text rather than assumed.
  const measureGrid = useCallback(() => {
    const node = body.current;
    if (!node) return { cols: 54, rows: 15 };
    const probe = document.createElement('span');
    probe.textContent = 'x'.repeat(100);
    probe.style.cssText = 'position:absolute;visibility:hidden;white-space:pre';
    node.appendChild(probe);
    const charWidth = probe.getBoundingClientRect().width / 100;
    probe.remove();
    const style = getComputedStyle(node);
    // Programs are drawn at a tighter leading than the transcript (see
    // .ox-prog), so the row count has to be measured against that line height
    // or the frame fills two thirds of the panel and floats in the top of it.
    const lineHeight = parseFloat(style.fontSize) * 1.15;
    const width = node.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const height = node.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
    return {
      cols: clamp(Math.floor(width / (charWidth || 8)), 28, 110),
      rows: clamp(Math.floor(height / lineHeight) - 1, 8, 26),
    };
  }, []);

  const stopProgram = useCallback((quietly) => {
    const active = running.current;
    if (!active) return;
    clearInterval(active.timer);
    running.current = null;
    setFrame(null);
    if (!quietly) print([L(`[ ${active.instance.label} exited ]`, 'dim')]);
  }, [print]);

  const PROGRAMS = useMemo(() => ({ matrix, donut, snake, top: topProgram }), []);

  const launch = useCallback((name) => {
    stopProgram(true);
    const grid = measureGrid();
    // `top` reads the workspace if a previous command already paid for it;
    // otherwise it runs on the seven systems the workspace is divided into.
    const taskList = tasks.current ?? [
      { name: 'kernel/syscalls', weight: 71 }, { name: 'kernel/sched', weight: 62 },
      { name: 'kernel/mm', weight: 48 }, { name: 'kernel/vfs', weight: 41 },
      { name: 'drivers/virtio', weight: 33 }, { name: 'kernel/net', weight: 28 },
      { name: 'arch/aarch64', weight: 19 }, { name: 'shared/sync', weight: 12 },
    ];
    const instance = name === 'top' ? PROGRAMS.top(grid, taskList) : PROGRAMS[name](grid);
    const tick = () => {
      const drawn = instance.step();
      if (drawn == null) { stopProgram(); return; }
      setFrame({ text: drawn, paint: instance.paint, label: instance.label });
    };
    tick();
    running.current = { instance, timer: setInterval(tick, 1000 / instance.fps) };
  }, [PROGRAMS, measureGrid, stopProgram]);

  useEffect(() => () => stopProgram(true), [stopProgram]);

  // While a program holds the screen it also holds the keyboard: q or escape
  // quits, and anything the program claims (the arrows, in snake) must not
  // also scroll the page underneath it.
  useEffect(() => {
    if (!frame) return;
    const onKey = (event) => {
      if (event.key === 'q' || event.key === 'Escape' || (event.ctrlKey && event.key === 'c')) {
        event.preventDefault();
        stopProgram();
        return;
      }
      const claimed = running.current?.instance.key?.(event.key);
      if (claimed) event.preventDefault();
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [frame, stopProgram]);

  // The boot banner types itself in, one line at a time. It is the page's
  // invitation: by the time it finishes, the caret is already blinking.
  useEffect(() => {
    const banner = commands.info.run([], {});
    const tail = [L(), L('type `help`, or run `snake`. this shell is real.', 'dim')];
    const all = [...bootScript(stats), ...banner, ...tail];
    if (reducedMotion()) { setLines(all); setBooted(true); return; }
    let at = 0;
    const timer = setInterval(() => {
      setLines(all.slice(0, at + 1));
      at += 1;
      if (at >= all.length) { clearInterval(timer); setBooted(true); }
    }, 78);
    return () => clearInterval(timer);
  }, [commands, stats]);

  useEffect(() => { if (body.current) body.current.scrollTop = body.current.scrollHeight; }, [lines, busy]);

  // Hand the cursor back when a program quits, so the prompt is ready to type
  // into rather than waiting to be clicked.
  useEffect(() => { if (!frame && booted) field.current?.focus({ preventScroll: true }); }, [frame, booted]);

  // Start typing anywhere on the page and the shell takes it. This is the
  // difference between a terminal you can use and one you have to find.
  useEffect(() => {
    const onKey = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.length !== 1) return;
      // A running program owns the keyboard. Without this the `q` that quits
      // it is also typed into the prompt waiting underneath.
      if (running.current) return;
      const active = document.activeElement;
      if (active === field.current) return;
      if (active && active !== document.body && active.tagName !== 'A') return;
      const box = shell.current?.getBoundingClientRect();
      if (!box || box.bottom < 60 || box.top > innerHeight - 60) return;
      // Take the keystroke as well as the focus. Focusing mid-keydown and
      // hoping the browser delivers the character loses the first letter of
      // whatever the reader just started typing.
      event.preventDefault();
      field.current?.focus();
      setInput((typed) => typed + event.key);
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);

  const run = useCallback(async (raw) => {
    const text = raw.trim();
    print([{ text, tone: 'you', prompt: `/${cwd}` }]);
    if (!text) return;
    history.current = [text, ...history.current.filter((item) => item !== text)].slice(0, 40);
    historyAt.current = -1;

    const [name, ...args] = text.split(/\s+/);
    const spec = commands[name.toLowerCase()];
    if (!spec) {
      const near = Object.keys(commands).find((key) => key.startsWith(name.slice(0, 2).toLowerCase()));
      print([L(`ox-sh: ${name}: command not found${near ? `. did you mean \`${near}\`?` : ''}`, 'warn')]);
      return;
    }

    const ctx = {
      cwd,
      print,
      setCwd,
      clear: () => setLines([]),
      go: onNavigate,
      panic: onPanic,
      toggleNeon: onNeon,
      workspace: getWorkspace,
      launch,
      wait: (ms) => new Promise((done) => setTimeout(done, ms)),
    };
    setBusy(true);
    try {
      const out = await spec.run(args, ctx);
      if (out?.length) print(out);
    } catch (cause) {
      print([L(`ox-sh: ${name}: ${cause.message ?? cause}`, 'warn')]);
    } finally {
      setBusy(false);
    }
  }, [commands, cwd, getWorkspace, launch, onNavigate, onNeon, onPanic, print]);

  // Drain the buffer the moment the prompt is free again.
  useEffect(() => {
    if (busy || !pending.current.length) return;
    run(pending.current.shift());
  }, [busy, run]);

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = input;
      setInput('');
      // A real terminal buffers what you type while something is running and
      // runs it when the prompt comes back. Dropping the line on the floor is
      // how you lose the command someone typed over a slow `build`.
      if (busy) pending.current.push(value);
      else run(value);
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      const partial = input.trim().toLowerCase();
      if (!partial || partial.includes(' ')) return;
      const hits = Object.keys(commands).filter((key) => key.startsWith(partial));
      if (!hits.length) return;
      if (hits.length === 1) { setInput(`${hits[0]} `); return; }
      // Several matches: fill in as far as they agree, then show the choice.
      // Completing to nothing and printing a list is how a shell wastes a tab.
      let common = hits[0];
      for (const hit of hits) {
        while (!hit.startsWith(common)) common = common.slice(0, -1);
      }
      if (common.length > partial.length) setInput(common);
      print([L(`  ${hits.join('  ')}`, 'dim')]);
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      if (!history.current.length) return;
      event.preventDefault();
      const next = event.key === 'ArrowUp'
        ? Math.min(history.current.length - 1, historyAt.current + 1)
        : Math.max(-1, historyAt.current - 1);
      historyAt.current = next;
      setInput(next === -1 ? '' : history.current[next]);
      return;
    }
    if (event.key === 'l' && event.ctrlKey) { event.preventDefault(); setLines([]); }
  };

  return (
    <div className="ox-term ox-term--live wx-scheme-dark" ref={shell} onClick={() => field.current?.focus()}>
      <div className="ox-term__bar">
        <span className="ox-term__dot" /><span className="ox-term__dot" /><span className="ox-term__dot" />
        <span className="ox-term__title">guest@oxide : ox-sh</span>
        <span className="ox-term__live">{busy ? 'working' : 'live'}</span>
      </div>

      <div className="ox-term__body" ref={body} role="log" aria-live="polite">
        {frame && <pre className={`ox-prog ox-prog--${frame.paint}`}>{frame.text}</pre>}

        {!frame && lines.map((line, index) => (
          <div className={`ox-line ox-line--${line.tone}`} key={index}>
            {line.tone === 'you' && <span className="ox-term__prompt">{line.prompt} $</span>}
            {line.text || ' '}
          </div>
        ))}

        {booted && !frame && (
          <div className="ox-line ox-line--edit">
            <span className="ox-term__prompt">/{cwd} $</span>
            <span className="ox-term__typed">{input}</span>
            <span className="ox-term__caret">▍</span>
            <input
              ref={field}
              className="ox-term__field"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={onKeyDown}
              aria-label="Oxide shell input"
              autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
            />
          </div>
        )}
      </div>

      <div className="ox-term__hints">
        {frame && (
          <span className="ox-term__quit">{frame.label} running · press q to quit</span>
        )}
        {!frame && ['snake', 'donut', 'matrix', 'top', 'help', 'crates'].map((cmd) => (
          <button type="button" key={cmd} className="ox-term__hint" disabled={busy}
            onClick={(event) => { event.stopPropagation(); setInput(''); run(cmd); field.current?.focus(); }}>
            {cmd}
          </button>
        ))}
      </div>
    </div>
  );
}
